import { useState, useEffect } from "react";
import "../../../styles/StoreBrowser.css";
import { base_url } from "../../../utils/apiConfig";
import { useFetchLaststores } from "../../../utils/hooks/StoreBrHook";
import { Button, Col, Form, Input, message, Row, Select, Table } from "antd";
import ScrollDown from "../../../components/ScrollDown";
import newStyled from "@emotion/styled";

const StoreBrowser = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { data: lastStoreData, isLoading: isLoadingLastStore } =
    useFetchLaststores("/store?lastStoreId=");

  const [filteredData, setFilteredData] = useState([]);
  const [noStoresMessage, setNoStoresMessage] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [visibleRows, setVisibleRows] = useState(20);
  const [filters, setFilters] = useState({
    storeCode: "",
    city: null,
    percentageFrom: "",
    percentageTo: "",
  });

  const [isLoading, setIsLoading] = useState(isLoadingLastStore);

  useEffect(() => {
    setIsLoading(isLoadingLastStore);
  }, [isLoadingLastStore]);

  const isValidScore = (score) => {
    return (
      score !== null &&
      score !== undefined &&
      score !== "" &&
      Number.isFinite(Number(score))
    );
  };

  const sortScoreWithNAAtEnd = (rows) => {
    return [...rows].sort((a, b) => {
      const aHasScore = isValidScore(a.totalScore);
      const bHasScore = isValidScore(b.totalScore);

      // Scored stores first
      if (aHasScore && !bHasScore) return -1;
      if (!aHasScore && bHasScore) return 1;

      // Keep existing order when both are N/A
      if (!aHasScore && !bHasScore) return 0;

      // Keep score ordering
      return Number(a.totalScore) - Number(b.totalScore);
    });
  };

  const data =
    lastStoreData?.stores_list
      ?.filter(
        (store) =>
          store.get_total_percentage?.score !== null &&
          store.get_total_percentage?.score !== undefined &&
          store.get_total_percentage?.score !== "",
      )
      .map((store) => ({
        id: store.id,
        storeCode: store.code || "---",
        showroomName: store.name,
        address: store.address,
        city: store.city.name,
        rank: store.get_store_rank,
        totalScore: store.get_total_percentage.score,
        color: store.get_total_percentage?.color,
        auditStoreCount: store.audit_store_count,
      })) || [];

  const cityListData =
    lastStoreData?.stores_list
      ?.reduce((acc, store) => {
        const cityName = store.city.name;
        if (!acc.some((item) => item.city_name === cityName)) {
          acc.push({
            city_id: store.city.id,
            city_name: cityName,
          });
        }
        return acc;
      }, [])
      .sort((a, b) => a.city_name.localeCompare(b.city_name)) || [];

  // const reversedData = [...data].reverse();
  const reversedData = sortScoreWithNAAtEnd([...data].reverse());
  const isFiltered = filteredData.length > 0 || noStoresMessage.length > 0;
  const activeData = isFiltered ? filteredData : reversedData;
  const currentData = activeData.slice(0, visibleRows);
  const handleSeeMore = () => {
    setVisibleRows((prev) => Math.min(prev + 20, activeData.length));
  };

  const handleReportBtn = (storeId) => {
    window.open(`/storeBrowserInsights/${storeId}`, "_blank");
  };

  const colorMapping = {
    1: "#ffc299", // 1-50%
    2: "#ffeb99", // 50-70%
    3: "#ff9", // 70-80%
    4: "#c1ff99", // 80-100%
    5: "#c1ff99", // 100%
    0: "#ffffff", // Default for null/0
  };

  const columns = [
    {
      title: <span style={{ textAlign: "center" }}>Sr. No.</span>,
      dataIndex: "index",
      key: "index",
      // render: (_, __, index) => (currentPage - 1) * rowsPerPage + index + 1,
      render: (_, __, index) => index + 1,
      width: 10,
      align: "center",
      onCell: () => ({
        style: { backgroundColor: "rgb(242, 242, 242)", textAlign: "center" },
      }),
    },
    {
      title: "Store Code",
      dataIndex: "storeCode",
      key: "storeCode",
      width: 40,
      align: "center",
    },
    {
      title: <div style={{ textAlign: "center" }}>Showroom Name</div>,
      dataIndex: "showroomName",
      key: "showroomName",
      width: 500,
      align: "left",
      render: (text) => (
        <div style={{ lineHeight: "1.5", whiteSpace: "pre-line" }}>{text}</div>
      ),
    },
    {
      title: <div style={{ textAlign: "center" }}>Address</div>,
      dataIndex: "address",
      key: "address",
      width: 1500,
      align: "left",
      render: (text) => (
        <div style={{ lineHeight: "1.5", whiteSpace: "pre-line" }}>{text}</div>
      ),
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      align: "center",
    },
    {
      title: <div style={{ textAlign: "center" }}>No. of Audits</div>,
      dataIndex: "auditStoreCount",
      key: "auditStoreCount",
      width: 250,
      align: "center",
      render: (text) => <div>{text ?? 0}</div>,
    },
    {
      title: <div style={{ textAlign: "center" }}>Total Score</div>,
      dataIndex: "totalScore",
      key: "totalScore",
      minWidth: 100,
      align: "center",
      render: (score, record) => {
        const getColor = (color) => colorMapping[color] || "#ffffff";
        const colorCode = record?.color ?? 0;
        const backgroundColor = getColor(colorCode);

        if (!isValidScore(score)) {
          return (
            <div
              className="progressBarContainer pt-1"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "40px",
              }}
            >
              <span>{score}</span>
            </div>
          );
        }

        return (
          <div className="progressBarContainer pt-1">
            <span>{score}</span>
            <div
              className="dash1Sb"
              style={{
                background: `linear-gradient(
            to right,
            ${backgroundColor} ${parseInt(score)}%,
            #e6e6e6 ${100 - parseInt(score)}%
          )`,
              }}
            ></div>
          </div>
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "id",
      key: "actions",
      align: "center",
      render: (id) => (
        <Button
          onClick={(e) => {
            // The row also opens the report; don't open it twice.
            e.stopPropagation();
            handleReportBtn(id);
          }}
          className="reportBtn"
        >
          View
        </Button>
      ),
    },
  ];

  const filterFields = [
    {
      label: <div className="filterLabel">Store Code:</div>,
      name: "storeCode",
      type: "text",
      placeholder: "Enter Store Code",
    },
    {
      label: <div className="filterLabel">City:</div>,
      name: "city",
      type: "text",
      placeholder: "Enter City",
    },
    {
      label: <div className="filterLabel">Percentage:</div>,
      name: "percentageFrom",
      type: "number",
      placeholder: "From",
      additional: "percentageTo",
    },
  ];

  const fetchFilteredData = (filters) => {
    setIsLoading(true);
    fetch(
      `${base_url.protocol}://${base_url.hostname}:${base_url.port}${base_url.api_base_path}client/filter_stores`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          store_code: filters.storeCode || "",
          selected_city: filters.city || "",
          percent_from: filters.percentageFrom || "",
          percent_to: filters.percentageTo || "",
        }),
      },
    )
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const transformedData = data.map((store) => ({
            id: store.id,
            storeCode: store.code || "----",
            showroomName: store.name,
            address: store.address,
            city: store.city?.name || "Unknown",
            rank: store.get_store_rank || "N/A",
            // totalScore: store.get_total_percentage?.score || "N/A",
            totalScore:
              store.get_total_percentage?.score !== null &&
              store.get_total_percentage?.score !== undefined
                ? store.get_total_percentage.score
                : "N/A",
            color: store.get_total_percentage?.color,
          }));
          let filteredResults = transformedData.filter((store) => {
            if (!isValidScore(store.totalScore)) {
              return false;
            }

            const score = Number(store.totalScore);
            const from = filters.percentageFrom
              ? Number(filters.percentageFrom)
              : 0;
            const to = filters.percentageTo
              ? Number(filters.percentageTo)
              : 100;

            return score >= from && score <= to;
          });

          filteredResults = sortScoreWithNAAtEnd(filteredResults);

          setFilteredData(filteredResults);
          setNoStoresMessage(
            filteredResults.length > 0 ? "" : "No stores found",
          );
        } else {
          setFilteredData([]);
          setNoStoresMessage("No stores found");
        }
      })
      .catch((error) =>
        console.log("Something wrong please check internet connection", error),
      )
      .finally(() => setIsLoading(false));
  };

  const handleFilterApply = () => {
    setVisibleRows(20);

    if (filters.percentageFrom && !filters.percentageTo) {
      return message.error("Please enter the To percentage.");
    }

    fetchFilteredData(filters);
  };

  const clearFilters = () => {
    setVisibleRows(20);

    setFilters({
      storeCode: "",
      city: "",
      percentageFrom: "",
      percentageTo: "",
    });

    setNoStoresMessage("");
    setFilteredData([]);
    fetchFilteredData({});
  };

  return (
    <div className="px-3 minHtForTbl">
      {/* Header & Filter */}
      <div className="head d-flex justify-content-between align-items-center">
        <div className="reportParaHeadSB">
          <p className="my-3">Store Browser</p>
        </div>

        <div className="d-flex">
          <div className="filterSection">
            <div
              className="filterSBr d-flex align-items-center gap-2 mx-2"
              onClick={() => setShowFilters(!showFilters)}
            >
              <p className="filterText my-1">Filter</p>
              <div className="filterIcon">
                <i className="bi bi-filter"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Modal */}
      {showFilters && (
        <div className="filterRow">
          <Row gutter={[8, 8]} className=" custom-row-sb w-100">
            {filterFields.map((field) => (
              <Col xs={24} sm={12} md={8} lg={6} key={field.name}>
                <Form.Item
                  label={field.label}
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                >
                  {field.name === "city" ? (
                    <SelectStyle
                      showSearch
                      placeholder="Select a city"
                      value={filters.city || undefined}
                      onChange={(value) =>
                        setFilters({
                          ...filters,
                          city: value,
                        })
                      }
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      size="medium"
                      className="store-city-select"
                      options={cityListData.map((city) => ({
                        label: city.city_name,
                        value: city.city_id,
                      }))}
                    />
                  ) : field.additional ? (
                    <div style={{ display: "flex" }}>
                      <InuptStyle
                        type="number"
                        size="medium"
                        value={filters[field.name]}
                        onChange={(e) =>
                          setFilters({
                            ...filters,
                            [field.name]: e.target.value,
                          })
                        }
                        placeholder={field.placeholder}
                      />
                      <span style={{ margin: "4px 8px" }}>to</span>
                      <InuptStyle
                        type="number"
                        size="medium"
                        value={filters[field.additional]}
                        onChange={(e) =>
                          setFilters({
                            ...filters,
                            [field.additional]: e.target.value,
                          })
                        }
                        placeholder="To"
                      />
                    </div>
                  ) : (
                    <InuptStyle
                      type={field.type}
                      size="medium"
                      value={filters[field.name]}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          [field.name]: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder={field.placeholder}
                    />
                  )}
                </Form.Item>
              </Col>
            ))}
            <Col
              xs={24}
              sm={12}
              md={8}
              lg={6}
              className="d-flex gap-2 store-broswer-btn"
            >
              <ButtonStyle
                type="primary"
                onClick={handleFilterApply}
                size="medium"
                style={{
                  fontFamily: "Roboto",
                  fontWeight: 500,
                }}
                disabled={isLoading}
              >
                {isLoading ? "Loading" : "Apply"}
              </ButtonStyle>
              <Button
                type="text"
                size="medium"
                onClick={() => {
                  clearFilters();
                }}
                style={{
                  color: "#007dc1",
                  fontFamily: "Roboto, sans-serif",
                  fontWeight: 500,
                }}
              >
                Clear Filter
              </Button>
            </Col>
          </Row>
        </div>
      )}

      {/* Table */}
      <div
        className="table-responsive "
        style={{ marginTop: showFilters ? "1rem" : "1rem", cursor: "pointer" }}
      >
        <TableStyle
          columns={columns}
          dataSource={currentData.map((row) => ({
            ...row,
            key: row.id,
          }))}
          loading={isLoading}
          pagination={false}
          rowKey="id"
          locale={{
            emptyText: noStoresMessage && (
              <p
                style={{
                  color: "#003d5c",
                  textAlign: "center",
                  padding: "2rem",
                  fontSize: "1rem",
                }}
              >
                {noStoresMessage}
              </p>
            ),
          }}
          onRow={(record) => ({
            onClick: () => handleReportBtn(record.id),
          })}
          scroll={{ x: 768 }}
        />
      </div>

      {activeData.length > 20 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "20px",
            marginTop: "1.5rem",
            marginBottom: "20px",
            marginRight: "0.5vw",
          }}
        >
          {/* Count */}
          <span
            style={{
              fontSize: "14px",
              color: "#353E4C",
              fontFamily: "Roboto, sans-serif",
              fontWeight: 500,
            }}
          >
            Showing {Math.min(visibleRows, activeData.length)} of{" "}
            {activeData.length}
          </span>

          {/* See More */}
          {visibleRows < activeData.length && (
            <Button onClick={handleSeeMore} className="reportBtn" size="medium">
              See More
            </Button>
          )}
        </div>
      )}
      <ScrollDown />
    </div>
  );
};

const InuptStyle = newStyled(Input)`
:where(.css-dev-only-do-not-override-1kf000u).ant-input{
    width:100% !important;
    max-width:90% !important;
}
    @media (max-width: 560px){
 :where(.css-dev-only-do-not-override-1kf000u).ant-input{
    max-width:100% !important;
}
}

:where(.css-dev-only-do-not-override-1kf000u).ant-input-outlined{
 border-color: #6c757d !important;
}
`;
const SelectStyle = newStyled(Select)`
:where(.css-dev-only-do-not-override-1kf000u).ant-select-outlined:not(.ant-select-customize-input) .ant-select-selector {
border-color: #6c757d !important;
}

:where(.css-dev-only-do-not-override-1kf000u).ant-select.ant-select-in-form-item {
width:100% !important;
max-width: 90% !important;
}

@media (max-width: 560px){
 :where(.css-dev-only-do-not-override-1kf000u).ant-select.ant-select-in-form-item {
  max-width: 100% !important;
}
}
`;
const TableStyle = newStyled(Table)`
  .ant-table {
    border-collapse: collapse !important;
  }

  .ant-table th, .ant-table td {
    white-space: nowrap;
  }

  .ant-table-thead > tr > th {
    position: sticky;
    top: 0;
    background: rgb(242, 242, 242) !important;
    z-index: 2;
  }

  :where(.css-dev-only-do-not-override-1kf000u).ant-table-wrapper table {
    border-collapse: collapse !important;
  }

  .table > :not(caption) > * > * {
    padding: 0 !important;
  }

  :where(.css-dev-only-do-not-override-1kf000u).ant-table-wrapper .ant-table-thead > tr > th {
    background-color: rgb(242, 242, 242) !important;
  }

  :where(.css-dev-only-do-not-override-1kf000u).ant-table-wrapper 
  .ant-table-thead > tr > th:first-child {
    background-color: rgb(242, 242, 242) !important; 
  }

  /* Background color for even and odd rows */
  .ant-table-tbody > tr:nth-child(odd) {
    background-color: rgb(242, 242, 242) !important;
  }

  .ant-table-tbody > tr:nth-child(even) {
    background-color: #ffffff !important;
  }

  /* Hover effect - both odd and even rows */
  .ant-table-tbody > tr:hover {
    background-color: rgb(242, 242, 242) !important;
  }
`;
const ButtonStyle = newStyled(Button)`
:where(.css-dev-only-do-not-override-1kf000u).ant-btn-lg {
border-radius: 5px !important;
background-color:#007dc1;
}
:where(.css-1kf000u).ant-btn-variant-solid {
background-color:#007dc1 !important;
}
`;

export default StoreBrowser;
